"use client";

import React from "react";
import { cn } from "../../lib/utils";
import { GlowingEffect } from "../GlowingEffect";

export interface PlanFeature {
  label: string;
  value: string | React.ReactNode;
}

export interface SupportItem {
  text: string;
}

export interface PlanCardProps {
  /** Plan name displayed as the title */
  title: string;
  /** Optional description below the title */
  description?: string;
  /** Badge text displayed at the top of the card (e.g., "Public Preview") */
  badge?: string;
  /** Main price to display */
  price: string | React.ReactNode;
  /** Price suffix (e.g., "/user/month") */
  priceSuffix?: string;
  /** Original price shown with strikethrough for promotions */
  originalPrice?: string;
  /** Optional pill/tag text below pricing (e.g., "Talk to our team") */
  pillText?: string;
  /** Pill variant for styling */
  pillVariant?: "green" | "orange" | "blue" | "purple";
  /** Key features displayed in the middle section */
  features?: PlanFeature[];
  /** Support and billing items */
  supportItems?: SupportItem[];
  /** CTA button text */
  ctaText: string;
  /** CTA button click handler */
  onCtaClick?: () => void;
  /** CTA button href (alternative to onClick) */
  ctaHref?: string;
  /** CTA button variant */
  ctaVariant?: "primary" | "outline";
  /** Enable glowing effect on hover */
  glowingEffect?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Card variant for styling differences */
  variant?: "default" | "featured";
}

const pillVariantClasses = {
  green:
    "bg-spectrum-green-light/30 dark:bg-spectrum-green-dark/30 text-lc-active dark:text-lc-active",
  orange:
    "bg-spectrum-orange-light/30 dark:bg-spectrum-orange-dark/30 text-lc-warning dark:text-lc-warning",
  blue: "bg-spectrum-blue-light/30 dark:bg-spectrum-blue-dark/30 text-teal dark:text-spectrum-blue-light",
  purple: "bg-plum/10 dark:bg-plum/30 text-plum dark:text-plum",
};

export const PlanCard: React.FC<PlanCardProps> = ({
  title,
  description,
  badge,
  price,
  priceSuffix,
  originalPrice,
  pillText,
  pillVariant = "green",
  features = [],
  supportItems = [],
  ctaText,
  onCtaClick,
  ctaHref,
  ctaVariant = "primary",
  glowingEffect = true,
  className,
}) => {
  const handleCtaClick = (e: React.MouseEvent) => {
    if (onCtaClick) {
      e.preventDefault();
      onCtaClick();
    }
  };

  const CtaComponent = ctaHref ? "a" : "button";
  const ctaProps = ctaHref
    ? { href: ctaHref }
    : { type: "button" as const, onClick: handleCtaClick };

  return (
    <div className={cn("relative h-full", className)}>
      {glowingEffect && (
        <GlowingEffect
          spread={60}
          glow={true}
          disabled={false}
          proximity={100}
          inactiveZone={0.5}
          className="rounded-2xl"
        />
      )}
      <div className="relative bg-background border rounded-2xl p-8 shadow-xs flex flex-col h-full">
        {/* Badge */}
        {badge && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <div className="bg-background border text-foreground px-4 py-2 rounded-full text-sm font-medium">
              {badge}
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="text-center mb-8 flex-none">
          <h3 className="text-4xl text-foreground mb-4 font-source h-10">
            {title}
          </h3>
          {description && (
            <div className="h-24 flex items-start justify-center pt-2">
              <p className="text-muted-foreground text-sm leading-relaxed">
                {description}
              </p>
            </div>
          )}
        </div>

        {/* Pricing Section */}
        <div className="text-center mb-8 flex-none">
          <div className="h-[60px] flex items-center justify-center mb-3">
            <div className="flex items-center justify-center gap-2">
              {originalPrice && (
                <span className="text-2xl font-bold text-muted-foreground line-through">
                  {originalPrice}
                </span>
              )}
              {typeof price === "string" ? (
                <span className="text-[40px] font-bold text-foreground">
                  {price}
                  {priceSuffix && (
                    <span className="text-sm font-normal text-muted-foreground">
                      {priceSuffix}
                    </span>
                  )}
                </span>
              ) : (
                <div className="text-4xl font-bold text-foreground">
                  {price}
                </div>
              )}
            </div>
          </div>

          {/* Pill Section */}
          {pillText && (
            <div className="flex justify-center">
              <div
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium inline-block",
                  pillVariantClasses[pillVariant],
                )}
              >
                {pillText}
              </div>
            </div>
          )}
        </div>

        {/* Features Section */}
        {features.length > 0 && (
          <div className="mb-8 flex-none">
            <div className="h-32 flex flex-col justify-start pt-2 space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span
                    className={cn(
                      "text-foreground",
                      index === 0 ? "text-base font-semibold" : "text-sm",
                    )}
                  >
                    {feature.label}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {typeof feature.value === "string"
                      ? feature.value
                      : feature.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support Section */}
        {supportItems.length > 0 && (
          <div className="flex-1">
            <h4 className="text-foreground mb-2">Support & Billing</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              {supportItems.map((item, index) => (
                <li key={index}>{item.text}</li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA Button */}
        <div className="flex-none relative mt-8">
          {glowingEffect && (
            <GlowingEffect
              spread={60}
              glow={true}
              disabled={false}
              proximity={20}
              inactiveZone={0.95}
              className="rounded-[12px]"
            />
          )}
          <CtaComponent
            {...ctaProps}
            className={cn(
              "w-full px-6 rounded-[10px] font-medium transition-colors backdrop-blur-sm h-[62px] text-base flex items-center justify-center",
              ctaVariant === "primary"
                ? "bg-foreground text-background hover:bg-foreground/90"
                : "border bg-background text-foreground hover:bg-muted",
            )}
          >
            {ctaText}
          </CtaComponent>
        </div>
      </div>
    </div>
  );
};

PlanCard.displayName = "PlanCard";
